import { Button, InlayPanel, Panel, Separator } from '@sorrem-client/ui/components';
import { resolveVeilSaturationById } from '@sorrem/shared';
import { useAppNavigate, useCharacterCommissions, useUpcomingCommissions } from '@sorrem-client/ui/hooks';
import { useCommissionAcceptance } from '@sorrem-client/ui/features/CharacterHome/components/CommissionOffers';
import { useCrucibleCatalog } from '@sorrem-client/ui/features/CharacterHome/hooks';
import { CommissionCard } from '../CommissionCard';
import { listShownCommissions } from '../../shown-commissions';
import { describeUpcomingCommission } from '../../upcoming-commission-text';
import styles from './FeaturedCruciblesPanel.module.scss';
import { toCrucibleAccentStyle } from '@sorrem-client/ui/features/CharacterHome/crucible-links';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

/** Highlighted crucibles and the character's commissions, shown beside the character on the Character tab. */
export function FeaturedCruciblesPanel(): JSX.Element {
    const navigate = useAppNavigate();
    const { crucibles } = useCrucibleCatalog();
    const { commissions, isLoading: isCommissionsLoading } = useCharacterCommissions();
    // The clerk's offer cutscene plays from Character Home's one owner, on this tab (#330).
    const { isAccepting, accept } = useCommissionAcceptance();
    const { upcoming } = useUpcomingCommissions();
    const shownCommissions = listShownCommissions(commissions);
    // Between commissions, what the next one needs shows under the last one completed.
    const isBetweenCommissions = shownCommissions.every((commission) => commission.status === 'completed');

    const handleSelectCrucible = (crucibleId: string): void => {
        navigate(`/character/crucibles/${encodeURIComponent(crucibleId)}`);
    };

    return (
        <Panel className={styles.featuredCruciblesPanel}>
            <section className={styles.section} {...toTourTargetProps('featured-crucibles')}>
                <h2 className={styles.heading}>Featured Crucibles</h2>

                <ul className={styles.crucibleList}>
                    {crucibles.map((crucible) => (
                        <li key={crucible.id}>
                            <InlayPanel
                                className={styles.crucibleCard}
                                style={toCrucibleAccentStyle(crucible.accentColor)}
                                onSelect={() => handleSelectCrucible(crucible.id)}
                            >
                                <p className={styles.crucibleName}>{crucible.name}</p>
                                <p className={styles.crucibleTier}>{resolveVeilSaturationById(crucible.veilSaturation).displayName}</p>
                            </InlayPanel>
                        </li>
                    ))}
                </ul>

                <Button
                    variant="secondary"
                    onClick={() => navigate('/character/crucibles')}
                    prominent
                >
                    Browse All Crucibles
                </Button>
            </section>

            <Separator />

            <section className={styles.section} {...toTourTargetProps('commissions')}>
                <h2 className={styles.heading}>Commissions</h2>

                {shownCommissions.length === 0 && !isCommissionsLoading && (
                    <p className={styles.emptyMessage}>No commissions right now.</p>
                )}
                {shownCommissions.map((commission) => (
                    <CommissionCard
                        key={commission.id}
                        commission={commission}
                        isAccepting={isAccepting}
                        onAccept={() => void accept(commission.commissionId)}
                    />
                ))}
                {isBetweenCommissions && upcoming.map((entry, index) => (
                    <p key={index} className={styles.upcomingNote}>{entry.note ?? describeUpcomingCommission(entry)}</p>
                ))}
            </section>

        </Panel>
    );
}
